import {
  type HeaderV3Props,
  LinksCategoryId,
  type LinksData,
} from '@jouzen/ecom-components';
import { type DynamicConfig, useDynamicConfig } from '@statsig/react-bindings';
import cloneDeep from 'lodash/cloneDeep';
import type { RichTranslationValues } from 'next-intl';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import getLocaleFormatting, {
  getLocaleFormatString,
} from '@/utils/getLocaleFormatting';

import isPathMatchToPOP from '../utils/isPathMatchToPOP';
import tEmBlockValue from '../utils/tEmBlockValue';
import tSupValue from '../utils/tSupValue';
import type { StsgLink } from './useLinkModifierConfig';
import useLinkModifierConfig from './useLinkModifierConfig';
import { useLocalizeLinks } from './useLocalizeLinks';

interface MenuProps extends Omit<DynamicConfig, 'value'> {
  value: {
    menuLinks: HeaderV3Props['menuLinks'];
  };
}

interface ModifyPOPLinksProps {
  activeMenuLinkConfig: boolean;
  statsigLinks: HeaderV3Props['menuLinks'];
  isGen3POP: boolean;
  isPOP: boolean;
  selectedMenuLink: unknown;
}

interface ModifyPOPImageLinkProps
  extends Omit<
    ModifyPOPLinksProps,
    'activeMenuLinkConfig' | 'selectedMenuLink'
  > {
  activeMenuImageConfig: boolean;
  selectedMenuImageLink: unknown;
}

const modifyPOPLinks = ({
  activeMenuLinkConfig,
  isGen3POP,
  isPOP,
  selectedMenuLink,
  statsigLinks,
}: ModifyPOPLinksProps) => {
  if (activeMenuLinkConfig && statsigLinks && isPOP) {
    const shopCatIndex = statsigLinks.findIndex(
      ({ id }) => id === LinksCategoryId.ShopCategory,
    );
    if (shopCatIndex && shopCatIndex !== -1) {
      const links = statsigLinks[shopCatIndex]?.links;
      const targetId = isGen3POP ? 'sale' : 'oura-ring';
      const _selectedMenuLink = selectedMenuLink as LinksData['link'];

      const linksIndex = links?.findIndex(({ id }) => id === targetId);
      if (
        links &&
        typeof linksIndex !== 'undefined' &&
        linksIndex !== -1 &&
        _selectedMenuLink
      ) {
        links.splice(linksIndex, 1, _selectedMenuLink);
      }
    }
  }
};

const modifyPOPImageLink = ({
  activeMenuImageConfig,
  isGen3POP,
  isPOP,
  selectedMenuImageLink,
  statsigLinks,
}: ModifyPOPImageLinkProps) => {
  const shouldModify = isPOP && !isGen3POP;
  if (activeMenuImageConfig && statsigLinks && shouldModify) {
    const imageIndex = statsigLinks.findIndex(
      ({ id }) => id === LinksCategoryId.MenuCard,
    );

    if (typeof imageIndex !== 'undefined' && imageIndex !== -1) {
      const _selectedMenuImageLink = selectedMenuImageLink as LinksData;
      statsigLinks.splice(imageIndex, 1, _selectedMenuImageLink);
    }
  }
};

const useLocalizeMenuImageLink = (
  statsigLink: StsgLink | undefined,
  tValues = {},
): StsgLink | undefined => {
  const t = useTranslations();

  if (!statsigLink) return;

  const localizedLink = cloneDeep(statsigLink);

  const _tValues: RichTranslationValues = {
    ...getLocaleFormatting,
    ...getLocaleFormatString,
    ...tValues,
  };

  if (localizedLink.label) {
    localizedLink.label = t.rich(
      localizedLink.label as MessageKey,
      _tValues,
    ) as string;
  }
  if (localizedLink.link) {
    localizedLink.link.label = t.rich(
      localizedLink.link.label as MessageKey,
      _tValues,
    ) as string;
    if (localizedLink.link.overlay) {
      localizedLink.link.overlay.title = t.rich(
        localizedLink.link.overlay.title as MessageKey,
        _tValues,
      ) as string;
      if (localizedLink.link.overlay.description) {
        localizedLink.link.overlay.description = t.rich(
          localizedLink.link.overlay.description as MessageKey,
          _tValues,
        ) as string;
      }
    }
  }

  return localizedLink;
};

const useMenuConfig = (pathname: string): HeaderV3Props['menuLinks'] => {
  // default menu config
  const config = useDynamicConfig('global_menu_links');
  const t = useTranslations();
  const menuConfigValue = config.value as MenuProps['value'];

  // determine if current path is gen3 or OR4 POP, or neither
  const { isGen3POP, isPOP } = isPathMatchToPOP(pathname);

  // menu link modifier config for POP
  const { isConfigActive: activeMenuLinkConfig, statsigLink } =
    useLinkModifierConfig('pop_modified_menu_link', isGen3POP);
  const selectedMenuLink = cloneDeep(statsigLink);
  if (selectedMenuLink?.label) {
    selectedMenuLink.label = t.rich(
      selectedMenuLink.label as MessageKey,
      tSupValue,
    ) as string;
  }

  // menu image modifier config for POP
  const {
    isConfigActive: activeMenuImageConfig,
    statsigLink: imageStatsigLink,
  } = useLinkModifierConfig('pop_modified_menu_image_link', isGen3POP);
  const defaultTValues = {
    ...tSupValue,
    ...tEmBlockValue,
  };
  const selectedMenuImageLink = useLocalizeMenuImageLink(
    imageStatsigLink,
    defaultTValues,
  );

  const statsigLinks = cloneDeep(menuConfigValue.menuLinks);

  // translate statsig link fields
  const localizedLinks = useLocalizeLinks(statsigLinks, defaultTValues);

  modifyPOPLinks({
    activeMenuLinkConfig,
    isGen3POP,
    isPOP,
    selectedMenuLink,
    statsigLinks: localizedLinks,
  });

  modifyPOPImageLink({
    activeMenuImageConfig,
    isGen3POP,
    isPOP,
    selectedMenuImageLink,
    statsigLinks: localizedLinks,
  });

  return localizedLinks;
};

export default useMenuConfig;
